import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getDvSourceImageUrl } from "../../functions/themeAssets";
import { normalizeCategoryName } from "./dragDropUtils";
import { getAudioQuestions } from "./stageContent";
import { StageLoader, StageShell, formatClock, useStageRun } from "./stageShared";
import { showAnswerFeedback } from "../nyPopups";

/** Stage 3 — audio quiz (Admin → Game Content → Audio), one track at a time. */
const STAGE_NUM = 3;
const DEFAULT_HEADING = "Audio Quiz";
const OPTION_LETTERS = ["A", "B", "C", "D"];
/** Waveform bar heights (0–1); the bars pulse while the clip plays. */
const WAVE = [
  0.25, 0.4, 0.3, 0.55, 0.7, 0.45, 0.85, 0.6, 0.95, 0.5, 0.75, 0.35, 0.6, 0.9, 0.55, 0.8, 0.4,
  0.65, 0.3, 0.7, 0.5, 0.85, 0.45, 0.6, 0.35, 0.75, 0.55, 0.9, 0.4, 0.6, 0.3, 0.5, 0.25, 0.4,
];

/** Vinyl + waveform + custom controls around a hidden <audio> element. */
function AudioDeck({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
  }, [src]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => setPlaying(false));
    else el.pause();
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    el.currentTime = (Number(e.target.value) / 100) * duration;
  };

  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div className={`aq-deck${playing ? " is-playing" : ""}`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        muted={muted}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
      />

      <div className="aq-visual">
        <button
          type="button"
          className="aq-vinyl"
          onClick={toggle}
          aria-label={playing ? "Pause track" : "Play track"}
        >
          <span className="aq-vinyl-disc" aria-hidden />
          <span className="aq-vinyl-btn" aria-hidden>
            <i className={playing ? "fa-solid fa-pause" : "fa-solid fa-play"} />
          </span>
        </button>
        <div className="aq-wave" aria-hidden>
          {WAVE.map((h, i) => (
            <span
              key={i}
              style={{ "--h": h, "--i": i, "--hue": 290 - (i / WAVE.length) * 110 }}
              className={i / WAVE.length <= progress / 100 ? "is-played" : ""}
            />
          ))}
        </div>
      </div>

      <div className="aq-controls">
        <button
          type="button"
          className="aq-ctrl"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
        >
          <i className={playing ? "fa-solid fa-pause" : "fa-solid fa-play"} />
        </button>
        <span className="aq-time">
          {formatClock(current)} / {formatClock(duration)}
        </span>
        <input
          type="range"
          className="aq-seek"
          min="0"
          max="100"
          step="0.1"
          value={progress}
          onChange={seek}
          style={{ "--p": `${progress}%` }}
          aria-label="Seek"
        />
        <button
          type="button"
          className="aq-ctrl aq-ctrl--plain"
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? "Unmute" : "Mute"}
        >
          <i className={muted ? "fa-solid fa-volume-xmark" : "fa-solid fa-volume-high"} />
        </button>
      </div>
    </div>
  );
}

export default function Stage3AudioQuiz() {
  const run = useStageRun(STAGE_NUM);
  const { themeData, isLoading, pointsPerCorrect, addPoints, finishStage, startTimer } = run;

  const heading = String(themeData?.audio_quiz_title || "").trim() || DEFAULT_HEADING;
  const subheading = String(themeData?.audio_quiz_subtitle || "").trim();
  const questions = useMemo(() => getAudioQuestions(themeData), [themeData]);

  const [qIndex, setQIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const answersRef = useRef([]);
  const finished = useRef(false);

  useEffect(() => {
    if (isLoading || finished.current) return;
    if (!questions.length) {
      finished.current = true;
      finishStage();
      return;
    }
    startTimer();
  }, [isLoading, questions.length, finishStage, startTimer]);

  const question = questions[qIndex] || null;

  const handleAnswer = useCallback(
    async (optionIndex) => {
      if (!question || busy || finished.current) return;
      setBusy(true);
      setSelected(optionIndex);
      document.querySelectorAll(".aq-deck audio").forEach((el) => el.pause());

      const userAnswer = question.options[optionIndex];
      const correct =
        normalizeCategoryName(userAnswer) === normalizeCategoryName(question.correctAnswer);
      answersRef.current.push({
        questionId: question.id,
        questionText: question.audio,
        userAnswer,
        correctAnswer: question.correctAnswer,
        isCorrect: correct,
        questionNumber: qIndex + 1,
      });
      if (correct) addPoints(pointsPerCorrect);
      setRevealed(true);

      await showAnswerFeedback({
        correct,
        points: pointsPerCorrect,
        answer: question.correctAnswer,
      });

      if (qIndex + 1 >= questions.length) {
        finished.current = true;
        finishStage({ answers: answersRef.current });
        return;
      }
      setQIndex((i) => i + 1);
      setSelected(null);
      setRevealed(false);
      setBusy(false);
    },
    [question, busy, qIndex, questions.length, pointsPerCorrect, addPoints, finishStage]
  );

  if (isLoading || !question) return <StageLoader />;

  const correctKey = normalizeCategoryName(question.correctAnswer);

  return (
    <StageShell run={run} heading={heading} subheading={subheading} step={qIndex}>
      <main key={question.id} className="ny-content aq-stage ny-navy-card">
        <AudioDeck src={getDvSourceImageUrl(question.audio)} />

        <div className="aq-options" role="group" aria-label="Answer options">
          {question.options.map((label, i) => {
            const isCorrect = normalizeCategoryName(label) === correctKey;
            let state = "";
            if (revealed && isCorrect) state = " is-correct";
            else if (revealed && selected === i) state = " is-wrong";
            return (
              <button
                key={`${question.id}-${i}`}
                type="button"
                className={`ny-option ny-option--dark${state}`}
                onClick={() => handleAnswer(i)}
                disabled={busy}
              >
                <span className="ny-option-letter">{OPTION_LETTERS[i]}</span>
                <span className="ny-option-label">{label}</span>
              </button>
            );
          })}
        </div>
      </main>
    </StageShell>
  );
}
